// DiscountSlabEditor.tsx — LBR2-A2 (2026-07-26)
// Reusable modal to view/set per-category discount slabs (autoApprove / maxApprove)
// for an employee OR a dealer OR an admin override. Endpoint-agnostic: the host
// passes the GET/PUT/DELETE paths. Shows the manager-cap hint per category and
// surfaces the 422 clamp reason when a slab exceeds the cap.
import { useEffect, useState, useCallback } from 'react';
import { api } from '@/utils/api';
import { X, Save, Loader2, Trash2, AlertTriangle } from 'lucide-react';

interface Slab { policyCategory: string; autoApprovePct: number; maxApprovePct: number }
interface Caps { autoApprovePct: number; maxApprovePct: number }

interface Props {
  title: string;
  subtitle?: string;
  /** GET path → { categories, slabs, managerCapsByCategory, adminOverride? } */
  getPath: string;
  /** PUT path, body { policyCategory|category, autoApprovePct, maxApprovePct } */
  putPath: string;
  /** DELETE path builder for a category (encodeURIComponent applied by caller). */
  deletePath: (category: string) => string;
  /** PUT body key for the category ('category' for team/dealer routes, 'policyCategory' for admin). */
  categoryKey?: 'category' | 'policyCategory';
  /** Extra body fields merged into every PUT (e.g. { userId } / { dealerId } for admin override). */
  putExtra?: Record<string, unknown>;
  onClose: () => void;
}

export function DiscountSlabEditor({
  title, subtitle, getPath, putPath, deletePath, categoryKey = 'category', putExtra, onClose,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>([]);
  const [slabByCat, setSlabByCat] = useState<Record<string, Slab | null>>({});
  const [capsByCat, setCapsByCat] = useState<Record<string, Caps | null>>({});
  const [adminOverride, setAdminOverride] = useState(false);
  const [draft, setDraft] = useState<Record<string, { auto: string; max: string }>>({});
  const [savingCat, setSavingCat] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.get(getPath);
      const d = res.data?.data ?? res.data ?? {};
      const cats: string[] = d.categories ?? [];
      const slabs: Slab[] = d.slabs ?? [];
      const caps: Record<string, Caps | null> = d.managerCapsByCategory ?? {};
      const byCat: Record<string, Slab | null> = {};
      const dr: Record<string, { auto: string; max: string }> = {};
      for (const c of cats) {
        const s = slabs.find((x) => x.policyCategory === c) ?? null;
        byCat[c] = s;
        dr[c] = { auto: s ? String(s.autoApprovePct) : '', max: s ? String(s.maxApprovePct) : '' };
      }
      setCategories(cats);
      setSlabByCat(byCat);
      setCapsByCat(caps);
      setAdminOverride(Boolean(d.adminOverride));
      setDraft(dr);
    } catch (err: any) {
      // SUN-40: show the reason (e.g. "That employee is not in your company.") instead of an empty table.
      setCategories([]);
      setLoadError(err?.response?.data?.error ?? 'Could not load the discount slabs.');
    } finally {
      setLoading(false);
    }
  }, [getPath]);

  useEffect(() => { load(); }, [load]);

  const saveRow = async (cat: string) => {
    const row = draft[cat];
    const auto = Number(row.auto);
    const max = Number(row.max);
    setRowError((e) => ({ ...e, [cat]: '' }));
    if (!Number.isFinite(auto) || !Number.isFinite(max) || auto < 0 || max < 0 || auto > 100 || max > 100 || auto > max) {
      setRowError((e) => ({ ...e, [cat]: 'Values must be 0–100 and auto ≤ max.' }));
      return;
    }
    setSavingCat(cat);
    try {
      await api.put(putPath, { [categoryKey]: cat, autoApprovePct: auto, maxApprovePct: max, ...(putExtra ?? {}) });
      await load();
    } catch (err: any) {
      const data = err?.response?.data;
      if (err?.response?.status === 422) {
        const clamp = data?.data?.clampedTo;
        setRowError((e) => ({
          ...e,
          [cat]: `${data?.error ?? 'Exceeds cap.'}${clamp ? ` (max allowed: auto ${clamp.autoApprovePct}% / max ${clamp.maxApprovePct}%)` : ''}`,
        }));
      } else {
        setRowError((e) => ({ ...e, [cat]: data?.error ?? 'Save failed.' }));
      }
    } finally {
      setSavingCat(null);
    }
  };

  const clearRow = async (cat: string) => {
    if (!confirm(`Remove the slab for "${cat}"? This category will then escalate (no auto-approve).`)) return;
    setSavingCat(cat);
    setRowError((e) => ({ ...e, [cat]: '' }));
    try {
      await api.delete(deletePath(encodeURIComponent(cat)));
      await load();
    } catch (err: any) {
      setRowError((e) => ({ ...e, [cat]: err?.response?.data?.error ?? 'Could not remove the slab.' }));
    } finally {
      setSavingCat(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
            {adminOverride && (
              <span className="mt-1 inline-block rounded bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                Admin override — no manager cap applied
              </span>
            )}
          </div>
          <button onClick={onClose} aria-label="Close"><X size={18} className="text-gray-400" /></button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin text-gray-400" size={22} /></div>
        ) : loadError ? (
          <div role="alert" className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
            <AlertTriangle size={14} className="shrink-0" /> {loadError}
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500">
                  <th className="py-1 font-medium">Category</th>
                  <th className="py-1 font-medium w-24">Auto %</th>
                  <th className="py-1 font-medium w-24">Max %</th>
                  <th className="py-1 font-medium">Cap</th>
                  <th className="py-1 font-medium w-24"></th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat) => {
                  const cap = capsByCat[cat];
                  const has = slabByCat[cat] != null;
                  const err = rowError[cat];
                  return (
                    <tr key={cat} className="border-t border-gray-50 align-top">
                      <td className="py-2 pr-2 text-gray-900">
                        {cat}
                        {!has && <span className="ml-1 text-[11px] text-gray-400">(no slab → escalates)</span>}
                        {err && (
                          <div className="mt-1 flex items-center gap-1 text-[11px] text-red-600">
                            <AlertTriangle size={11} /> {err}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-2">
                        <input
                          type="number" min={0} max={100}
                          className="w-20 rounded border border-gray-300 px-2 py-1 text-sm"
                          value={draft[cat]?.auto ?? ''}
                          onChange={(e) => setDraft((d) => ({ ...d, [cat]: { ...d[cat], auto: e.target.value } }))}
                        />
                      </td>
                      <td className="py-2 pr-2">
                        <input
                          type="number" min={0} max={100}
                          className="w-20 rounded border border-gray-300 px-2 py-1 text-sm"
                          value={draft[cat]?.max ?? ''}
                          onChange={(e) => setDraft((d) => ({ ...d, [cat]: { ...d[cat], max: e.target.value } }))}
                        />
                      </td>
                      <td className="py-2 pr-2 text-xs text-gray-500">
                        {adminOverride ? '—' : cap ? `≤ ${cap.autoApprovePct}/${cap.maxApprovePct}%` : 'none → escalate'}
                      </td>
                      <td className="py-2">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => saveRow(cat)}
                            disabled={savingCat === cat}
                            className="flex items-center gap-1 rounded bg-[var(--ink-0)] px-2 py-1 text-xs font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
                          >
                            {savingCat === cat ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                          </button>
                          {has && (
                            <button onClick={() => clearRow(cat)} disabled={savingCat === cat} className="text-gray-400 hover:text-red-500" aria-label="Clear slab">
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-4 flex justify-end">
          <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Close</button>
        </div>
      </div>
    </div>
  );
}
